import React, { useEffect } from "react";
import QuickNavigation from "../components/common/quick-navigation";
import RecentActivityPanel from "../components/common/recent-activity-panel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faBug,
  faUsersGear,
  faPlaneUp,
} from "@fortawesome/free-solid-svg-icons";
import { Box, Typography, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useHasPermission } from "../hooks/useHasPermission";
import { useTenant } from "../contexts/tenant-context";
import { usePermissions } from "../contexts/permission-context";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();
  const { permissions, loading } = usePermissions();
  const { isGlobalAdmin, selectedTenantId, selectedTenant } = useTenant();
  const canAccessUserManagement = useHasPermission("accessmanagement", "read");
  const canAccessOnboarding = useHasPermission("onboarding", "read");
  const canAccessIataDocuments = useHasPermission("documents-search", "read");
  const canAccessNasDocuments = useHasPermission("documents-nas", "read");
  const canAccessDocuments = canAccessIataDocuments || canAccessNasDocuments;
  const canAccessDocumentUpload = useHasPermission(
    "documents-upload",
    "create",
  );
  const canAccessAssetsComponent = useHasPermission(
    "assets-assemblies",
    "read",
  );
  const canAccessAssetsAirframe = useHasPermission("assets-airframes", "read");
  const canAccessAssetsBulkTemplate = useHasPermission(
    "assets-bulk-template-upload",
    "read"
  );
  const canAccessMiniPackExport = useHasPermission("assets-mini-pack-export", "read");
  const canAccessDocumentMove = useHasPermission("documents-move", "read");
  const canAccessAssets =
    canAccessAssetsComponent ||
    canAccessAssetsAirframe ||
    canAccessAssetsBulkTemplate;

  // Only skip permissions check if user is acting as global admin (selected Global Admin option)
  const isActingAsGlobalAdmin =
    isGlobalAdmin && selectedTenantId === "__GLOBAL_ADMIN__";
  const isTenantAdmin = selectedTenant?.role?.toLowerCase() === "admin";

  // Check if user has any permissions at all (non-global admin only)
  useEffect(() => {
    // Skip check while loading or for users acting as global admins
    if (loading || isActingAsGlobalAdmin) return;

    // If no permissions (null or no tenant selected), redirect to no-access
    if (!permissions) {
      navigate("/no-access", { state: { reason: "no-tenant" } });
      return;
    }

    // Check if user has at least one read permission
    const hasAnyPermission = Object.values(permissions).some(
      (feature) => feature.read === true,
    );

    // If no permissions at all, redirect to no-access page
    if (!hasAnyPermission) {
      navigate("/no-access", { state: { reason: "no-permissions" } });
    }
  }, [loading, isActingAsGlobalAdmin, permissions, navigate]);

  // Show loading spinner while permissions are being checked
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If no permissions loaded (no tenant selected or invalid session),
  // return null while the useEffect redi rect to /no-access takes effect
  if (!permissions && !isActingAsGlobalAdmin) {
    return null;
  }

  const documentActions = [
    { label: "Search", route: "/document/search" },
    ...(canAccessDocumentUpload
      ? [{ label: "Upload", route: "/document/upload" }]
      : []),
  ];

  const exportActions = [
    ...(canAccessDocumentMove
      ? [{ label: "Move Jobs", route: "/document/move-jobs" }]
      : []),
    ...(canAccessIataDocuments
      ? [{ label: "Export Jobs", route: "/document/exports" }]
      : []),
    ...(canAccessMiniPackExport
      ? [{ label: "Export Mini-Pack Jobs", route: "/assets/mini-pack-generator?scope=all&label=All" }]
      : []),
  ];

  const adminActions = [
    { label: "User Access", route: "/userAccess" },
    ...(!isActingAsGlobalAdmin && isTenantAdmin
      ? [{ label: "Tenant Management", route: "/access-management/tenant-management" }]
      : []),
    ...(isActingAsGlobalAdmin
      ? [{ label: "Tenant Access", route: "/on-board/tenant-management" }]
      : []),
  ];

  const assetsActions = [
    ...(canAccessAssetsAirframe
      ? [{ label: "Manage Airframe", route: "/assets/manage-airframe" }]
      : []),
    ...(canAccessAssetsComponent
      ? [
          { label: "Manage Assemblies", route: "/assets/manage-component" },
          { label: "Manage Sub-Assemblies", route: "/assets/manage-sub-assembly" },
        ]
      : []),
    ...(canAccessAssetsBulkTemplate
      ? [{ label: "Bulk Template Upload", route: "/assets/bulk-template-upload" }]
      : []),
  ];

  const shouldShowActivityPanel =
    isActingAsGlobalAdmin ||
    (!!selectedTenantId && selectedTenantId !== "__GLOBAL_ADMIN__");

  // Check if there are any accessible features
  const hasAnyAccessibleFeature =
    canAccessOnboarding ||
    canAccessAssets ||
    canAccessDocuments ||
    canAccessUserManagement ||
    true;

  return (
    <Box sx={{ width: "100%", mt: 3 }}>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, xl: shouldShowActivityPanel ? 7 : 12 }}>
          <Typography
            variant="h6"
            align="center"
            fontWeight={700}
            color="#fff"
            sx={{ mb: 3 }}
          >
            Quick Navigation
          </Typography>
          {!hasAnyAccessibleFeature ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "40vh",
              }}
            >
              <Typography variant="body1" color="#fff" align="center">
                No accessible features found. Please contact your administrator.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 3,
                justifyContent: "center",
                gridTemplateColumns: {
                  xs: "minmax(280px, 1fr)",
                  lg: "repeat(2, minmax(320px, 360px))",
                },
              }}
            >
              {isActingAsGlobalAdmin && (
                <Box sx={{ minWidth: 350, height: "100%" }}>
                  <QuickNavigation
                    icon={<FontAwesomeIcon icon={faPlaneUp} />}
                    actions={[
                      {
                        label: "Upload Airframe Template",
                        route: "/airframe-template-upload",
                      },
                    ]}
                    title="Airframe Templates"
                  />
                </Box>
              )}

              {!isActingAsGlobalAdmin && canAccessAssets && (
                <Box sx={{ minWidth: 350, height: "100%" }}>
                  <QuickNavigation
                    icon={<FontAwesomeIcon icon={faPlaneUp} />}
                    actions={assetsActions}
                    title="Asset Management"
                  />
                </Box>
              )}

              {!isActingAsGlobalAdmin && canAccessDocuments && (
                <Box sx={{ minWidth: 350, height: "100%", justifySelf: { lg: "center" } }}>
                  <QuickNavigation
                    icon={<FontAwesomeIcon icon={faFile} />}
                    actions={documentActions}
                    title="Document Management"
                  />
                </Box>
              )}

              {!isActingAsGlobalAdmin && exportActions.length > 0 && (
                <Box sx={{ minWidth: 350, height: "100%" }}>
                  <QuickNavigation
                    icon={<FontAwesomeIcon icon={faFile} />}
                    actions={exportActions}
                    title="Jobs"
                  />
                </Box>
              )}

              {canAccessUserManagement && (
                <Box sx={{ minWidth: 350, height: "100%" }}>
                  <QuickNavigation
                    icon={<FontAwesomeIcon icon={faUsersGear} />}
                    actions={adminActions}
                    title="Access Management"
                  />
                </Box>
              )}

              <Box sx={{ minWidth: 350, height: "100%" }}>
                <QuickNavigation
                  icon={<FontAwesomeIcon icon={faBug} />}
                  actions={[
                    {
                      label: "Bug Report",
                      route: "/app-management/bug-reports",
                    },
                    {
                      label: "Audit Log",
                      route: "/app-management/audit-logs",
                    },
                  ]}
                  title="App Management"
                />
              </Box>
            </Box>
          )}
        </Grid>

        {shouldShowActivityPanel ? (
          <Grid size={{ xs: 12, xl: 5 }}>
            <Typography
              variant="h6"
              align="center"
              fontWeight={700}
              color="#fff"
              sx={{ mb: 3 }}
            >
              Recent Activities
            </Typography>

            <RecentActivityPanel />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default Dashboard;
